<template>
  <div v-if="visible" class="modal-backdrop">
    <div class="modal">
      <h1 class="modal-title">Descarrega el QR per a:</h1>
      <div :class="qrClassName" ref="qrRef" class="qr-container">
        <p class="difunt-nom">{{ nomDifunt }}</p>

        <p v-if="nomSala" class="sala-nom text-menor">Sala: {{ nomSala }}</p>

        <p v-if="dataCerimonia" class="cerimonia text-menor">
          {{ formatDataHora(dataCerimonia) }}
        </p>

        <p v-if="nomParroquiaNet" class="parroquia text-menor">
          {{ nomParroquiaNet }}
        </p>

        <p
          v-if="llocCerimonia && llocCerimonia !== 'Parròquia'"
          class="cerimonia text-menor"
        >
          {{ llocCerimonia }}
        </p>

        <div v-if="qrVetllaValue" class="qr-wrapper">
          <qrcode-vue :value="qrVetllaValue" :size="170" :level="'H'" />
        </div>
      </div>

      <div class="modal-buttons">
        <button class="btn" @click="descarregar">Descarrega</button>
        <button class="btn" @click="imprimir">Imprimir QR</button>
        <button class="btn btn-secondary" @click="$emit('close')">Tanca</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import QrcodeVue from "qrcode.vue";
import useQr from "../qr";
import { Vetlla, Sala } from "../types/vetlla.ts";
import { computed, ref, watch, onMounted, onUnmounted } from "vue";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import axios from "axios";

const qrRef = ref<HTMLElement | null>(null);
const llocCerimonia = ref("");
const parroquia = ref("");

const generateQrPdf = async (): Promise<jsPDF | null> => {
  if (!qrRef.value) return null;

  qrRef.value.classList.add("qr-pdf-mode");

  const excludes = qrRef.value.querySelectorAll(".exclude-from-pdf");
  excludes.forEach((el) => ((el as HTMLElement).style.display = "none"));

  const canvas = await html2canvas(qrRef.value, {
    backgroundColor: "#ffffff",
    scale: 3,
    useCORS: true,
  });

  excludes.forEach((el) => ((el as HTMLElement).style.display = ""));

  qrRef.value.classList.remove("qr-pdf-mode");

  const qrDataUrl = canvas.toDataURL("image/png");

  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "cm",
    format: [20.8, 29.7],
  });

  const cols = 2;
  const rows = 3;
  const cardWidth = 10.4;
  const cardHeight = 9.9;
  const marginX = 1.5;
  const marginY = 1.8;
  const qrWidth = cardWidth - marginX * 2;
  const qrHeight = cardHeight - marginY * 2;
  const topOffset = 1.5;

  let i = 0;
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const x = col * cardWidth + marginX;
      const y = row * cardHeight + marginY - topOffset;

      pdf.addImage(qrDataUrl, "PNG", x, y, qrWidth, qrHeight);
      i++;
      if (i >= 6) break;
    }
  }

  return pdf;
};

const descarregar = async () => {
  const pdf = await generateQrPdf();
  if (pdf) {
    pdf.save(`qr-${nomDifunt.value}.pdf`);
  }
};

const imprimir = async () => {
  const pdf = await generateQrPdf();
  if (pdf) {
    const blobUrl = URL.createObjectURL(pdf.output("blob"));
    const win = window.open(blobUrl, "_blank");
    if (win) {
      win.onload = () => {
        win.print();
      };
    }
  }
};

const props = defineProps<{
  visible: boolean;
  vetlla: Vetlla | null;
}>();

const baseUrl = import.meta.env.VITE_HOST;
const {
  setQrVals,
  obertQrVal,
  tancatQrVal,
  obertQrClassName,
  tancatQrClassName,
} = useQr(baseUrl);

const qrValue = ref("");
const qrVetllaValue = ref("");
const nomDifunt = ref("");
const nomSala = ref("");
const qrClassName = computed(() =>
  props.vetlla?.IsQrObert ? obertQrClassName : tancatQrClassName,
);
const dataCerimonia = ref<Date | null>(null);
const salas = ref<Sala[]>([]);

const formatDataHora = (data: Date): string => {
  const dia = data.getDate().toString().padStart(2, "0");
  const hores = data.getHours().toString().padStart(2, "0");
  const minuts = data.getMinutes().toString().padStart(2, "0");
  return `Cerimònia dia ${dia} a les ${hores}:${minuts}h`;
};

const nomParroquiaNet = computed(() => {
  if (!parroquia.value) return "";
  return parroquia.value.replace(/^\d+\s*/, "");
});

watch(
  () => props.vetlla,
  async (vetlla) => {
    if (vetlla) {
      nomDifunt.value = vetlla.NombreDifunto;
      setQrVals(vetlla.Id);

      qrValue.value = vetlla.IsQrObert ? obertQrVal.value : tancatQrVal.value;
      qrVetllaValue.value = `${baseUrl}/upload/vetlla/${vetlla.Id}`;

      llocCerimonia.value = vetlla.LlocCerimonia || "";
      parroquia.value = vetlla.Parroquia || "";

      if (vetlla.DataCerimonia) {
        const data = parseFechaString(vetlla.DataCerimonia);
        if (data) dataCerimonia.value = toDatetimeLocalValue(data);
      }

      const salaResponse = await axios.get("/api/sala/index");
      salas.value = salaResponse.data;
      const salaVetlla = salas.value.find((s) => s.IdVetlla === vetlla.Id);
      nomSala.value = salaVetlla ? salaVetlla.Nombre : "";
    }
  },
  { immediate: true },
);

const parseFechaString = (fechaRaw: string): Date | null => {
  const regex = /^(\d{2})\/(\d{2})\/(\d{4})[ T](\d{2}):(\d{2})$/;
  const match = fechaRaw.match(regex);
  if (!match) return null;

  const [, dia, mes, any, hores, minuts] = match;
  return new Date(`${any}-${mes}-${dia}T${hores}:${minuts}`);
};

const toDatetimeLocalValue = (date: Date): Date => {
  return date;
};

const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === "Escape" && props.visible) {
    emit("close");
  }
};

onMounted(() => {
  window.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  window.removeEventListener("keydown", handleKeydown);
});

const emit = defineEmits<{
  (e: "close"): void;
}>();
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}
.modal {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  text-align: center;
  max-width: 500px;
}
.modal-title {
  margin-bottom: 1rem;
  font-size: 1.25rem;
}

.difunt-nom {
  font-size: 1.8rem;
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.qr-container {
  font-size: 1.8rem;
  margin: 1rem 0;
}

.qr-container p {
  font-size: 1.8rem;
  margin: 0.6rem 0;
}

.modal-buttons {
  margin-top: 2rem;
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
}
.btn {
  padding: 0.5rem 1.2rem;
  min-width: 120px;
  text-align: center;
  border: none;
  background-color: #2b6cb0;
  color: white;
  border-radius: 6px;
  cursor: pointer;
}
.btn-secondary {
  background-color: #718096;
}
.qr-container qrcode-vue {
  width: 100px;
  height: 100px;
  display: block;
  margin: 1.5rem auto;
}
.qr-container canvas {
  width: 100px;
  height: 100px;
  display: block;
  margin: 1.5rem auto;
}
.qr-wrapper {
  margin-top: 2rem;
}
.text-menor {
  font-size: 1.1rem;
  color: #555;
}
@media print {
  .qr-container canvas {
    width: 5cm;
    height: 5cm;
  }

  .qr-container p {
    font-size: 1.8rem;
  }

  .difunt-nom {
    font-size: 1.8rem;
  }
}
.qr-pdf-mode .difunt-nom {
  font-size: 2.2rem;
}

.qr-pdf-mode .qr-container p {
  font-size: 2rem;
}

.qr-pdf-mode .qr-container canvas {
  width: 5cm;
  height: 5cm;
}
</style>
